import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { itemLayout, locationImageForViewport, ownedItemsNewestFirst, type ItemPoint } from "../shared/location.js";
import { ShopItem } from "../shared/shop-items.js";
import { StoreController } from "./controller.store.js";
import { backIcon } from "./icons.js";
import { playItemClickSound, playItemDropSound } from "./item-click-sounds.js";
import { navigate } from "./nav.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";

const DESKTOP_QUERY = "(hover: hover) and (pointer: fine) and (min-width: 801px)";
const DRAG_THRESHOLD = 8;

@customElement("reading-bee-location")
export class ReadingBeeLocation extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        height: 100%;
        max-height: 100%;
        overflow: hidden;
        background: #0c0b09;
      }

      .page {
        position: relative;
        height: 100%;
        overflow: hidden;
        background: #0c0b09;
      }

      .page::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 15;
        height: 7rem;
        pointer-events: none;
        background: linear-gradient(to bottom, rgba(12, 11, 9, 0.5), transparent);
      }

      .scene {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        pointer-events: none;
        user-select: none;
        -webkit-user-drag: none;
      }

      .back {
        position: absolute;
        top: calc(0.7rem + var(--inset-top));
        left: 1rem;
        z-index: 30;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--color-primary-text);
        background: rgba(12, 11, 9, 0.55);
        border: 1px solid rgba(244, 234, 213, 0.28);
        backdrop-filter: blur(10px);
      }

      .back:hover {
        background: rgba(12, 11, 9, 0.78);
        border-color: var(--color-1);
      }

      .playground {
        display: none;
        position: absolute;
        inset: 0;
      }

      .prop {
        position: absolute;
        left: 0;
        top: 0;
        width: clamp(88px, 12vw, 150px);
        padding: 0;
        background: none;
        border: 0;
        cursor: grab;
        touch-action: none;
        transform: translate(-50%, -50%);
        filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.35));
      }

      .prop img {
        display: block;
        width: 100%;
        height: auto;
        pointer-events: none;
        user-select: none;
        -webkit-user-drag: none;
        transform-origin: center;
      }

      .prop:hover:not(.dragging):not(.popping) img {
        animation: itemWiggle 2.4s ease-in-out infinite;
      }

      .prop.dragging {
        cursor: grabbing;
        z-index: 40 !important;
        filter: drop-shadow(0 16px 24px rgba(0, 0, 0, 0.45));
      }

      .prop.popping img {
        animation: itemPop 560ms ease;
      }

      .strip {
        position: absolute;
        inset: 0;
        z-index: 2;
        display: flex;
        gap: 1.4rem;
        align-items: center;
        scrollbar-width: thin;
      }

      .strip .prop {
        position: relative;
        left: auto;
        top: auto;
        transform: none;
        cursor: pointer;
        flex: 0 0 auto;
        touch-action: manipulation;
      }

      @media (orientation: portrait) {
        .strip {
          flex-direction: column;
          justify-content: flex-start;
          overflow-x: hidden;
          overflow-y: auto;
          padding: calc(5rem + var(--inset-top)) 1.2rem calc(2rem + env(safe-area-inset-bottom));
        }

        .strip .prop {
          width: min(58vw, 240px);
        }
      }

      @media (orientation: landscape) {
        .strip {
          flex-direction: row;
          justify-content: flex-start;
          overflow-x: auto;
          overflow-y: hidden;
          padding: calc(4.4rem + var(--inset-top)) calc(1.2rem + env(safe-area-inset-right))
            calc(1.2rem + env(safe-area-inset-bottom)) calc(1.2rem + env(safe-area-inset-left));
        }

        .strip .prop {
          width: min(36vh, 220px);
        }
      }

      @media (hover: hover) and (pointer: fine) and (min-width: 801px) {
        .playground {
          display: block;
        }

        .strip {
          display: none;
        }
      }

      @keyframes itemWiggle {
        0%,
        100% {
          transform: scale(1.12) rotate(-4deg);
        }
        50% {
          transform: scale(1.16) rotate(4deg);
        }
      }

      @keyframes itemPop {
        0% {
          transform: scale(1) rotate(0deg);
        }
        35% {
          transform: scale(1.22) rotate(-8deg);
        }
        70% {
          transform: scale(0.94) rotate(6deg);
        }
        100% {
          transform: scale(1) rotate(0deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .prop:hover:not(.dragging):not(.popping) img,
        .prop.popping img {
          animation: none;
        }
      }
    `,
  ];

  @state() private sceneSrc = locationImageForViewport(
    window.innerWidth,
    window.innerHeight,
    appStore.currentProfile?.currentLocationId,
  );
  @state() private poppingId: string | null = null;
  @state() private draggingId: string | null = null;
  @state() private livePositions: Record<string, ItemPoint> = {};
  private dragMoved = false;
  private dragStart: { x: number; y: number; itemX: number; itemY: number } | null = null;
  private desktopQuery: MediaQueryList | null = null;

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.syncScene();
    window.addEventListener("resize", this.syncScene);
    window.addEventListener("orientationchange", this.syncScene);
    this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.desktopQuery.addEventListener("change", this.syncScene);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.syncScene);
    window.removeEventListener("orientationchange", this.syncScene);
    this.desktopQuery?.removeEventListener("change", this.syncScene);
    window.removeEventListener("pointermove", this.onWindowPointerMove);
    window.removeEventListener("pointerup", this.onWindowPointerUp);
    window.removeEventListener("pointercancel", this.onWindowPointerUp);
  }

  override render(): TemplateResult {
    const profile = appStore.currentProfile;
    if (!profile) {
      return html`
        <div class="page"><p>No profile selected.</p></div>
      `;
    }
    const items = ownedItemsNewestFirst(profile.inventory);
    const saved = { ...profile.itemPositions, ...this.livePositions };
    const layout = itemLayout(
      items.map((item) => item.id),
      saved,
    );
    const byId = new Map(layout.map((entry) => [entry.id, entry]));
    return html`
      <div class="page">
        <img class="scene" src=${this.sceneSrc} alt="" />
        <button class="back" aria-label="Back to shop" @click=${this.goShop}>${backIcon}</button>
        <div class="playground">${items.map((item) => this.prop(item, byId.get(item.id), true))}</div>
        <div class="strip">${items.map((item) => this.prop(item, byId.get(item.id), false))}</div>
      </div>
    `;
  }

  private prop(
    item: ShopItem,
    placed: { x: number; y: number; z: number } | undefined,
    desktop: boolean,
  ): TemplateResult {
    const x = placed?.x ?? 0.2;
    const y = placed?.y ?? 0.84;
    const z = placed?.z ?? 1;
    const classes = ["prop", this.draggingId === item.id ? "dragging" : "", this.poppingId === item.id ? "popping" : ""]
      .filter(Boolean)
      .join(" ");
    if (!desktop) {
      return html`
        <button class=${classes} aria-label=${item.name} @click=${() => this.play(item.id)}>
          <img src=${item.image} alt=${item.name} draggable="false" />
        </button>
      `;
    }
    return html`
      <button
        class=${classes}
        style="left:${x * 100}%;top:${y * 100}%;z-index:${z}"
        aria-label=${item.name}
        @pointerdown=${(event: PointerEvent) => this.onPointerDown(event, item.id, x, y)}>
        <img src=${item.image} alt=${item.name} draggable="false" />
      </button>
    `;
  }

  private goShop = (): void => {
    navigate("shop");
  };

  private syncScene = (): void => {
    this.sceneSrc = locationImageForViewport(
      window.innerWidth,
      window.innerHeight,
      appStore.currentProfile?.currentLocationId,
    );
  };

  private isDesktop(): boolean {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  private onPointerDown = (event: PointerEvent, id: string, x: number, y: number): void => {
    if (!this.isDesktop() || event.button !== 0) return;
    event.preventDefault();
    this.draggingId = id;
    this.dragMoved = false;
    this.dragStart = { x: event.clientX, y: event.clientY, itemX: x, itemY: y };
    window.addEventListener("pointermove", this.onWindowPointerMove);
    window.addEventListener("pointerup", this.onWindowPointerUp);
    window.addEventListener("pointercancel", this.onWindowPointerUp);
  };

  private onWindowPointerMove = (event: PointerEvent): void => {
    if (!this.draggingId || !this.dragStart) return;
    const dx = event.clientX - this.dragStart.x;
    const dy = event.clientY - this.dragStart.y;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      if (!this.dragMoved) playItemDropSound();
      this.dragMoved = true;
    }
    const stage = this.renderRoot.querySelector(".playground");
    if (!(stage instanceof HTMLElement)) return;
    const rect = stage.getBoundingClientRect();
    const x = this.dragStart.itemX + dx / Math.max(1, rect.width);
    const y = this.dragStart.itemY + dy / Math.max(1, rect.height);
    this.livePositions = {
      ...this.livePositions,
      [this.draggingId]: {
        x: Math.min(0.96, Math.max(0.04, x)),
        y: Math.min(0.96, Math.max(0.04, y)),
      },
    };
  };

  private onWindowPointerUp = (): void => {
    const id = this.draggingId;
    const moved = this.dragMoved;
    const live = id ? this.livePositions[id] : undefined;
    this.draggingId = null;
    this.dragStart = null;
    window.removeEventListener("pointermove", this.onWindowPointerMove);
    window.removeEventListener("pointerup", this.onWindowPointerUp);
    window.removeEventListener("pointercancel", this.onWindowPointerUp);
    if (!id) return;
    if (moved && live) {
      playItemDropSound();
      appStore.setItemPosition(id, live.x, live.y);
      const next = { ...this.livePositions };
      delete next[id];
      this.livePositions = next;
      return;
    }
    this.play(id);
  };

  private play(id: string): void {
    playItemClickSound();
    this.poppingId = id;
    window.setTimeout(() => {
      if (this.poppingId === id) this.poppingId = null;
    }, 560);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-location": ReadingBeeLocation;
  }
}
